export const TEMPLATE_VARIABLES = [
  "user_name",
  "user_email",
  "action_url",
  "action_label",
  "token",
  "package_name",
  "invoice_number",
  "amount",
  "currency",
  "subscription_start",
  "subscription_end",
  "renewal_date",
  "payment_status",
  "company_name",
  "company_address",
  "invoice_url",
  "profile_url",
  "support_email",
  "message",
] as const;

export type TemplateVariables = Record<(typeof TEMPLATE_VARIABLES)[number], string>;

export const sampleVariables: TemplateVariables = {
  user_name: "Client Delapresse",
  user_email: "client@example.com",
  action_url: "https://delapresse.fr/reset-password",
  action_label: "Open Delapresse",
  token: "123456",
  package_name: "News Site Pro",
  invoice_number: "DLP-2026-0001",
  amount: "49.00",
  currency: "EUR",
  subscription_start: "2026-05-05",
  subscription_end: "2026-06-05",
  renewal_date: "2026-06-05",
  payment_status: "paid",
  company_name: "Delapresse",
  company_address: "Adresse de la societe",
  invoice_url: "https://dashboard.stripe.com/test/invoices",
  profile_url: "https://delapresse.fr/profile",
  support_email: "info@delapresse.fr",
  message: "A Delapresse system event requires attention.",
};

export function renderTemplateString(input = "", variables: Partial<TemplateVariables> = sampleVariables) {
  return input.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: keyof TemplateVariables) => variables[key] ?? "");
}

const emailTypes = [
  ["payment_success", "Payment success", "Paiement reussi"],
  ["invoice_issued", "Invoice issued", "Facture emise"],
  ["payment_failed", "Payment failed", "Paiement echoue"],
  ["subscription_renewed", "Subscription renewed", "Abonnement renouvele"],
  ["subscription_confirmation", "Subscription confirmation", "Confirmation abonnement"],
  ["subscription_cancelled", "Subscription cancelled", "Abonnement annule"],
  ["package_upgraded", "Package upgraded", "Package augmente"],
  ["package_downgraded", "Package downgraded", "Package reduit"],
  ["password_reset", "Password reset", "Reinitialisation du mot de passe"],
  ["signup_confirmation", "Signup confirmation", "Confirmation inscription"],
  ["magic_link", "Magic link / OTP", "Lien magique / OTP"],
  ["invitation", "Invitation", "Invitation"],
  ["email_change", "Email change", "Changement email"],
  ["account_created", "Account created", "Compte cree"],
  ["account_deletion_rgpd", "Account deletion / RGPD", "Suppression compte / RGPD"],
  ["marketing_newsletter", "Marketing newsletter", "Newsletter marketing"],
  ["marketing_comparison", "Marketing comparison", "Comparatif marketing"],
  ["system_notification", "System notification", "Notification systeme"],
  ["package_expiration_warning", "Package expiration warning", "Avertissement expiration package"],
] as const;

export type DefaultEmailTemplate = {
  id?: string;
  template_key: string;
  name: string;
  email_type: string;
  language: "fr" | "en";
  subject: string;
  from_name: string;
  from_email: string;
  html_body: string;
  plain_text_body: string;
  variables: string[];
  preview_variables?: Partial<TemplateVariables>;
  category?: string;
  description?: string | null;
  is_enabled: boolean;
  updated_at?: string;
};

export const defaultEmailTemplates: DefaultEmailTemplate[] = emailTypes.flatMap(([type, enName, frName]) => [
  {
    template_key: `${type}_fr`,
    name: `${frName} FR`,
    email_type: type,
    language: "fr",
    subject: subjectFor(type, "fr"),
    from_name: "Delapresse",
    from_email: "info@delapresse.fr",
    html_body: bodyFor(type, "fr", true),
    plain_text_body: bodyFor(type, "fr", false),
    variables: [...TEMPLATE_VARIABLES],
    preview_variables: sampleVariables,
    category: categoryFor(type),
    is_enabled: true,
  },
  {
    template_key: `${type}_en`,
    name: `${enName} EN`,
    email_type: type,
    language: "en",
    subject: subjectFor(type, "en"),
    from_name: "Delapresse",
    from_email: "info@delapresse.fr",
    html_body: bodyFor(type, "en", true),
    plain_text_body: bodyFor(type, "en", false),
    variables: [...TEMPLATE_VARIABLES],
    preview_variables: sampleVariables,
    category: categoryFor(type),
    is_enabled: true,
  },
]);

export function getDefaultEmailTemplate(emailType: string, language: "fr" | "en" = "fr") {
  return defaultEmailTemplates.find((template) => template.email_type === emailType && template.language === language)
    ?? defaultEmailTemplates.find((template) => template.email_type === emailType && template.language === "fr")
    ?? null;
}

export function getDefaultInvoiceTemplate(language: "fr" | "en" = "fr") {
  return defaultInvoiceTemplates.find((template) => template.language === language)
    ?? defaultInvoiceTemplates[0]
    ?? null;
}

function subjectFor(type: string, language: "fr" | "en") {
  const fr: Record<string, string> = {
    payment_success: "Paiement Delapresse confirme",
    invoice_issued: "Votre facture {{invoice_number}}",
    payment_failed: "Paiement Delapresse echoue",
    subscription_renewed: "Votre abonnement est renouvele",
    subscription_confirmation: "Votre abonnement Delapresse est actif",
    subscription_cancelled: "Votre abonnement est annule",
    package_upgraded: "Votre package Delapresse a ete augmente",
    package_downgraded: "Votre package Delapresse a ete reduit",
    password_reset: "Reinitialisation de votre mot de passe",
    signup_confirmation: "Confirmez votre compte Delapresse",
    magic_link: "Votre lien de connexion Delapresse",
    invitation: "Vous etes invite a rejoindre Delapresse",
    email_change: "Confirmez votre changement d'adresse email",
    account_created: "Bienvenue sur Delapresse",
    account_deletion_rgpd: "Votre compte Delapresse a ete supprime",
    marketing_newsletter: "Confirmez votre abonnement Delapresse",
    marketing_comparison: "Votre comparatif Delapresse",
    system_notification: "Notification Delapresse",
    package_expiration_warning: "Votre package expire bientot",
  };
  const en: Record<string, string> = {
    payment_success: "Delapresse payment confirmed",
    invoice_issued: "Your invoice {{invoice_number}}",
    payment_failed: "Delapresse payment failed",
    subscription_renewed: "Your subscription was renewed",
    subscription_confirmation: "Your Delapresse subscription is active",
    subscription_cancelled: "Your subscription was cancelled",
    package_upgraded: "Your Delapresse package was upgraded",
    package_downgraded: "Your Delapresse package was downgraded",
    password_reset: "Reset your password",
    signup_confirmation: "Confirm your Delapresse account",
    magic_link: "Your Delapresse sign-in link",
    invitation: "You are invited to join Delapresse",
    email_change: "Confirm your Delapresse email change",
    account_created: "Welcome to Delapresse",
    account_deletion_rgpd: "Your Delapresse account has been deleted",
    marketing_newsletter: "Confirm your Delapresse newsletter subscription",
    marketing_comparison: "Your Delapresse plan comparison",
    system_notification: "Delapresse notification",
    package_expiration_warning: "Your package expires soon",
  };
  return (language === "fr" ? fr : en)[type] ?? "Delapresse";
}

function categoryFor(type: string) {
  if (type.startsWith("marketing_")) return "marketing";
  if (["payment_success", "invoice_issued", "payment_failed", "subscription_renewed", "subscription_confirmation"].includes(type)) return "billing";
  if (["password_reset", "signup_confirmation", "magic_link", "invitation", "email_change", "account_created"].includes(type)) return "auth";
  return "system";
}

function bodyFor(type: string, language: "fr" | "en", html: boolean) {
  if (type === "password_reset") {
    return html
      ? `<h1>Reset your password</h1><p>Hello {{user_name}}, use the button to reset your Delapresse password.</p>`
      : `Hello {{user_name}}, reset your Delapresse password: {{action_url}}`;
  }
  const line = language === "fr"
    ? `Bonjour {{user_name}}, ${subjectFor(type, "fr").toLowerCase()}. Package: {{package_name}}. Facture: {{invoice_number}}. Montant: {{amount}} {{currency}}. Renouvellement: {{renewal_date}}.`
    : `Hello {{user_name}}, ${subjectFor(type, "en").toLowerCase()}. Package: {{package_name}}. Invoice: {{invoice_number}}. Amount: {{amount}} {{currency}}. Renewal: {{renewal_date}}.`;
  return html ? `<p>${line}</p><p><a href="{{invoice_url}}">Invoice</a> - <a href="{{profile_url}}">Profile</a></p>` : `${line}\nInvoice: {{invoice_url}}\nProfile: {{profile_url}}`;
}

export type DefaultInvoiceTemplate = {
  id?: string;
  template_key: string;
  name: string;
  language: "fr" | "en";
  company_name: string;
  company_logo_url: string | null;
  company_legal_details: string | null;
  footer_text: string | null;
  payment_terms: string | null;
  html_body: string;
  variables: string[];
  is_default: boolean;
  is_enabled: boolean;
};

export const defaultInvoiceTemplates: DefaultInvoiceTemplate[] = [
  {
    template_key: "default_invoice_fr",
    name: "Facture standard FR",
    language: "fr",
    company_name: "Delapresse",
    company_logo_url: "{{company_logo_url}}",
    company_legal_details: "SIRET / TVA / mentions legales a completer",
    footer_text: "Merci pour votre confiance.",
    payment_terms: "Paiement effectue par Stripe.",
    html_body: invoiceHtml("fr"),
    variables: [...TEMPLATE_VARIABLES, "line_items", "subtotal", "tax", "total", "company_logo_url"],
    is_default: true,
    is_enabled: true,
  },
  {
    template_key: "default_invoice_en",
    name: "Standard invoice EN",
    language: "en",
    company_name: "Delapresse",
    company_logo_url: "{{company_logo_url}}",
    company_legal_details: "SIRET / VAT / legal details to complete",
    footer_text: "Thank you for your business.",
    payment_terms: "Paid by Stripe.",
    html_body: invoiceHtml("en"),
    variables: [...TEMPLATE_VARIABLES, "line_items", "subtotal", "tax", "total", "company_logo_url"],
    is_default: true,
    is_enabled: true,
  },
];

function invoiceHtml(language: "fr" | "en") {
  const labels = language === "fr"
    ? { invoice: "Facture", customer: "Client", package: "Package", period: "Periode", subtotal: "Sous-total", tax: "Taxe", total: "Total", status: "Statut" }
    : { invoice: "Invoice", customer: "Customer", package: "Package", period: "Billing period", subtotal: "Subtotal", tax: "Tax", total: "Total", status: "Status" };
  return `
<section class="invoice">
  <header><img src="{{company_logo_url}}" alt="{{company_name}}" style="max-height:48px" /><h1>${labels.invoice} {{invoice_number}}</h1></header>
  <p><strong>{{company_name}}</strong><br />{{company_address}}</p>
  <p>${labels.customer}: {{user_name}} - {{user_email}}</p>
  <p>${labels.package}: {{package_name}}</p>
  <p>${labels.period}: {{subscription_start}} - {{subscription_end}}</p>
  <table><tbody>{{line_items}}</tbody></table>
  <p>${labels.subtotal}: {{subtotal}} {{currency}}</p>
  <p>${labels.tax}: {{tax}} {{currency}}</p>
  <h2>${labels.total}: {{total}} {{currency}}</h2>
  <p>${labels.status}: {{payment_status}}</p>
  <footer>{{company_name}} - {{profile_url}}</footer>
</section>`;
}
